import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { State } from '../types/app/state'
import { DisplayMode } from '../types/app/displayMode'
import type { Configuration } from '../types/configuration/Configuration'
import { SupportRequestState } from '../types/app/supportRequestState'
import { OauthOption } from '../types/auth/oauthOption'

export const useAppStore = defineStore('appStore', () => {
  const configuration = ref<Configuration | null>(null)
  const displayMode = ref<DisplayMode>(DisplayMode.Light)
  const currentState = ref<State>(State.Login) // default
  const currentSupportRequestState = ref<SupportRequestState>(SupportRequestState.Closed)
  const selectedProduct = ref<string | undefined>(undefined)
  const productFromUrl = ref<string | undefined>(undefined)
  const supportRequestButtonVisible = ref<boolean>(false)
  const selectedLanguage = ref<string>('en')
  const oauthProvider = ref<OauthOption>()

  // Persistent storage using browser storage
  const LOCAL_STORAGE_KEY = 'aida_client_preferences'
  const REDIRECT_STORAGE_KEY = 'app-store-redirect'

  function setSelectedProduct(product: string): void {
    selectedProduct.value = product
    console.log('Product selected in store: ', product)
  }
  function setProductFromUrl(product: string): void {
    productFromUrl.value = product
    console.log('Product from url set in store: ', product)
  }

  function switchMode() {
    displayMode.value =
      displayMode.value === DisplayMode.Light ? DisplayMode.Dark : DisplayMode.Light
  }

  function switchModeTo(): string {
    return displayMode.value === DisplayMode.Light ? DisplayMode.Dark : DisplayMode.Light
  }

  function currentMode(): string {
    return displayMode.value
  }

  function setConfiguration(config: Configuration): void {
    configuration.value = config
  }

  function setState(state: State) {
    currentState.value = state
    console.log(`appStore - set_state to ${state}`)
  }

  function setSupportRequestState(state: SupportRequestState) {
    currentSupportRequestState.value = state
  }

  function setLanguage(lang: string) {
    selectedLanguage.value = lang
  }

  function setOauthProvider(provider: OauthOption) {
    oauthProvider.value = provider
  }

  function saveToLocalStorage() {
    const data = {
      displayMode: displayMode.value,
      language: selectedLanguage.value,
      selectedProduct: selectedProduct.value,
    }
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data))
    console.log(`Preferences saved: ${LOCAL_STORAGE_KEY} = ${data.displayMode}`)
  }

  function loadFromLocalStorage() {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        if (parsed.displayMode != displayMode.value) {
          document
            .getElementsByTagName('html')[0]
            .setAttribute(
              'data-theme',
              currentMode() === DisplayMode.Light ? DisplayMode.Dark : DisplayMode.Light
            )
          console.log('Switch Display Mode using persistent preferences')
          switchMode()
        }
        if (parsed.language) selectedLanguage.value = parsed.language
        if (parsed.selectedProduct) selectedProduct.value = parsed.selectedProduct
        if (parsed.oauthProvider) oauthProvider.value = parsed.oauthProvider
      } catch (e) {
        console.warn('Failed to parse stored preferences in the browser storage', e)
      }
    }
  }

  function saveForRedirect(): void {
    try {
      const payload = {
        currentState: currentState.value,
        currentSupportRequestState: currentSupportRequestState.value,
        selectedProduct: selectedProduct.value,
        productFromUrl: productFromUrl.value,
        oauthProvider: oauthProvider.value,
      }
      sessionStorage.setItem(REDIRECT_STORAGE_KEY, JSON.stringify(payload))
    } catch (e) {
      console.warn('Failed to save app state for redirect', e)
    }
  }

  function restoreAfterRedirect(): void {
    try {
      const raw = sessionStorage.getItem(REDIRECT_STORAGE_KEY)
      if (!raw) return

      const parsed = JSON.parse(raw)

      currentState.value = parsed.currentState
      currentSupportRequestState.value = parsed.currentSupportRequestState
      selectedProduct.value = parsed.selectedProduct
      productFromUrl.value = parsed.productFromUrl
      oauthProvider.value = parsed.oauthProvider
    } catch (e) {
      console.warn('Failed to restore app state after redirect', e)
    } finally {
      sessionStorage.removeItem(REDIRECT_STORAGE_KEY)
    }
  }

  // Watch values changes and save to storage when changed
  watch([displayMode, selectedLanguage, selectedProduct], saveToLocalStorage, { deep: true })

  // Load the values from local storage when the appStore is created
  loadFromLocalStorage()

  return {
    configuration,
    currentState, //returns application state
    currentSupportRequestState,
    supportRequestButtonVisible,
    oauthProvider,
    currentMode, //returns current mode
    switchModeTo, //returns the value that will be used after switch
    switchMode, //switch the mode
    setConfiguration, //sets the login mode if it is enabled, enabled for SR only, or disabled
    setState, //sets application state, for example login, main,
    setSupportRequestState,
    setOauthProvider,
    setProductFromUrl,
    productFromUrl,
    setSelectedProduct,
    selectedProduct,
    setLanguage,
    selectedLanguage,
    saveForRedirect,
    restoreAfterRedirect,
  }
})
